import KpiChart from "@app/components/kpi-chart";
import { useToast } from "@app/components/toast";
import { environment } from "@app/relay/relay-environment";
import * as Dialog from "@radix-ui/react-dialog";
import type { walletPage_Query } from "@app/queries/walletPage_Query.graphql";
import type { walletPage_RepromoteMutation } from "@app/queries/walletPage_RepromoteMutation.graphql";
import type { walletPage_LinkMutation } from "@app/queries/walletPage_LinkMutation.graphql";
import { useEffect, useMemo, useState } from "react";
import { commitMutation, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import WalletCounterparties from "./wallet-counterparties";
import WalletPromotionProgress from "./wallet-promotion-progress";
import WalletUploader from "./wallet-uploader";
import WalletActionsMenu from "./wallet-actions-menu";
import WalletManageFilesDialog from "./wallet-manage-files-dialog";
import WalletLedgerDialog from "./wallet-ledger-dialog";

export const walletQuery = graphql`
  query walletPage_Query($slug: String!, $perspectiveId: ID) {
    wallet(slug: $slug, perspectiveId: $perspectiveId) {
      slug
      name
      bankAccount {
        id
        number
        nickname
      }
      balance {
        month
        value
      }
      inflows {
        month
        value
      }
      outflows {
        month
        value
      }
      healthAlarms {
        kind
        period
        message
        amount
      }
      reconciliation {
        rec
        date
        funded
        spent
        delta
        fundingMatched
      }
      uploads {
        files {
          filename
        }
      }
      shape {
        message
        landedKind
        landedNumber
        providers
        expected
        importedTxns
        importedFirst
        importedLast
      }
    }
    sourcingAccounts {
      qboId
      name
      merged
      keptSeparate
      nearTwinCount
      suggestion {
        id
        number
        nickname
      }
      run {
        jobId
        kind
        status
        phase
        currentObject
        errorMessage
      }
    }
  }
`;

// Link-at-promotion: QuickBooks' own account for this wallet, suggested by its
// records near-matching the wallet's rows. Merging is a background run.
const linkMutation = graphql`
  mutation walletPage_LinkMutation($action: String!, $qboAccountQboId: String!, $bankAccountId: ID) {
    sourcingAccountMerge(action: $action, qboAccountQboId: $qboAccountQboId, bankAccountId: $bankAccountId) {
      ok
      job {
        jobId
        status
      }
      errors {
        message
      }
    }
  }
`;

// Re-run the whole promotion over every staged file — the "Re-promote all" action.
// Returns a run the progress bar then polls.
const repromoteMutation = graphql`
  mutation walletPage_RepromoteMutation($slug: String!) {
    walletRepromote(slug: $slug) {
      job {
        status
      }
      errors {
        message
      }
    }
  }
`;

type Flow = "inflows" | "outflows";

const FLOWS: { key: Flow; label: string; color: string }[] = [
  { key: "inflows", label: "Money in", color: "#16a34a" },
  { key: "outflows", label: "Money out", color: "#dc2626" },
];

export default function WalletPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<walletPage_Query>;
}) {
  const data = usePreloadedQuery(walletQuery, queryRef);
  const wallet = data.wallet;
  const notify = useToast();

  // Bump to make the progress bar re-poll immediately after an action starts a run.
  const [pollKey, setPollKey] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [visible, setVisible] = useState<Record<Flow, boolean>>({
    inflows: true,
    outflows: true,
  });

  const hasFiles = wallet.uploads.files.length > 0;

  // A finished promotion means fresh ledger/counterparty data landed — reload so
  // every section (grid, chart, ledger) reflects it.
  const onPromoted = () => window.location.reload();

  const afterUploadStarted = () => {
    setUploadOpen(false);
    setPollKey((k) => k + 1);
  };

  // The QuickBooks account whose records near-match this wallet — the moment a
  // promotion creates the account is when the unmerged window opens, so the
  // prompt lives here, on the wallet, not on a data-health check a week later.
  const linkable = wallet.bankAccount
    ? data.sourcingAccounts.find(
        (row) => !row.merged && !row.keptSeparate && row.suggestion?.number === wallet.bankAccount?.number,
      )
    : null;
  const linkRun = linkable?.run && linkable.run.status === "running" ? linkable.run : null;
  useEffect(() => {
    if (!linkRun) return;
    const timer = setInterval(() => window.location.reload(), 4000);
    return () => clearInterval(timer);
  }, [linkRun]);

  const link = (action: "MERGE" | "KEEP_SEPARATE") => {
    if (!linkable) return;
    commitMutation<walletPage_LinkMutation>(environment, {
      mutation: linkMutation,
      variables: {
        action,
        qboAccountQboId: linkable.qboId,
        bankAccountId: action === "MERGE" ? linkable.suggestion?.id ?? null : null,
      },
      onCompleted: (res) => {
        const err = res.sourcingAccountMerge?.errors?.[0]?.message;
        if (err) {
          notify({ message: err, kind: "error" });
          return;
        }
        notify({
          message:
            action === "MERGE"
              ? `Linking QuickBooks' ${linkable.name} to this wallet in the background…`
              : `${linkable.name} is kept separate — no more suggestions for it.`,
          kind: "info",
        });
        window.location.reload();
      },
      onError: (e) => notify({ message: e.message, kind: "error" }),
    });
  };

  const repromote = () => {
    commitMutation<walletPage_RepromoteMutation>(environment, {
      mutation: repromoteMutation,
      variables: { slug: wallet.slug },
      onCompleted: (res) => {
        const err = res.walletRepromote?.errors?.[0]?.message;
        if (err) {
          notify({ message: err, kind: "error" });
          return;
        }
        notify({ message: `Re-promoting ${wallet.name} in the background…`, kind: "info" });
        setPollKey((k) => k + 1);
      },
      onError: (e) => notify({ message: e.message, kind: "error" }),
    });
  };

  // Balance line defines the shared X-axis; each flow bar gets its own stack.
  const series = useMemo(
    () => [
      { name: "Balance", points: wallet.balance, type: "line" as const, color: "#111827" },
      ...FLOWS.filter((f) => visible[f.key]).map((f) => ({
        name: f.label,
        points: wallet[f.key],
        type: "bar" as const,
        stack: f.key,
        color: f.color,
      })),
    ],
    [wallet, visible]
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{wallet.name} wallet</h1>
          <p className="text-sm text-gray-500">
            Who was paid through {wallet.name}, funded from your bank — upload the
            CSV exports and they promote into the ledger.
          </p>
        </div>
        <WalletActionsMenu
          onShowLedger={() => setLedgerOpen(true)}
          ledgerDisabled={!hasFiles}
          onUploadMore={() => setUploadOpen(true)}
          onManageFiles={() => setManageOpen(true)}
          onRepromote={repromote}
          repromoteDisabled={!hasFiles}
        />
      </div>

      {/* Progress bar sits directly below the title and above the table. */}
      <WalletPromotionProgress slug={wallet.slug} />

      {/* Link-at-promotion. QuickBooks books these payments too; until its
          account is merged onto this wallet, every record without a cents-exact
          twin enters the ledger as a second cost. */}
      {linkable && (
        <div className="space-y-2 rounded border border-indigo-300 bg-indigo-50 p-4 text-sm text-indigo-900">
          {linkRun ? (
            <p>
              <span className="font-medium">Linking QuickBooks' {linkable.name} to this wallet…</span>{" "}
              {linkRun.currentObject ?? linkRun.phase ?? "queued"}
            </p>
          ) : (
            <>
              <p>
                <span className="font-medium">QuickBooks' "{linkable.name}" looks like this wallet</span>
                {linkable.nearTwinCount > 0 && (
                  <>
                    {" "}
                    — <span className="tabular-nums font-medium">{linkable.nearTwinCount.toLocaleString()}</span> of its
                    records match {wallet.name} transactions within fees and FX
                  </>
                )}
                . Until they are linked, QuickBooks' copy of each payment counts as a second cost.
              </p>
              {linkable.run?.status === "error" && (
                <p className="text-xs text-red-700">The last link attempt failed: {linkable.run.errorMessage}</p>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => link("MERGE")}
                  className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                >
                  Link them
                </button>
                <button
                  type="button"
                  onClick={() => link("KEEP_SEPARATE")}
                  className="rounded border border-indigo-300 px-3 py-1.5 text-xs text-indigo-800 hover:bg-indigo-100"
                >
                  Not the same account
                </button>
                <span className="text-xs text-indigo-600">
                  Linking is reversible and changes no totals — matched payments stop counting twice.
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Why there is no balance, when the files landed somewhere that is not a
          wallet. /wallet/deel has been in exactly this state since its first upload
          — invoice summaries build a billing ledger, not a fundable balance — and
          the page said nothing, so the emptiness read as a broken import and was
          chased with seventeen promotion runs that could never have fixed it. */}
      {wallet.shape && (
        <div className="space-y-3 rounded border border-sky-300 bg-sky-50 p-4">
          <h2 className="font-semibold text-sky-900">
            Your {wallet.name} files imported — just not as a wallet
          </h2>

          {/* The two halves side by side. The import SUCCEEDED and its rows are live;
              stating only the empty half reads as a failed import, which is the
              misreading that got chased with seventeen promotion runs. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-xs font-semibold tracking-wide text-emerald-800 uppercase">
                Imported
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-emerald-900">
                {wallet.shape.importedTxns.toLocaleString()}
              </p>
              <p className="text-xs text-emerald-800">
                transactions in {wallet.shape.landedNumber ?? "an account"}
                {wallet.shape.landedKind && ` (${wallet.shape.landedKind})`}
              </p>
              {wallet.shape.importedFirst && wallet.shape.importedLast && (
                <p className="mt-1 text-xs text-emerald-700">
                  {wallet.shape.importedFirst} → {wallet.shape.importedLast}
                </p>
              )}
              <p className="mt-1 text-xs text-emerald-700">
                Feeding the ledger, the P&amp;L and CAC.
              </p>
            </div>

            <div className="rounded border border-gray-300 bg-white p-3">
              <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                Wallet balance
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-400">—</p>
              <p className="text-xs text-gray-500">
                No {wallet.name} wallet account exists, so there is nothing to chart here.
              </p>
              {wallet.shape.expected && (
                <p className="mt-1 text-xs text-gray-500">
                  Needs a <span className="font-mono">{wallet.shape.expected}</span> export.
                </p>
              )}
            </div>
          </div>

          <p className="text-sm text-sky-900">{wallet.shape.message}</p>
          <p className="text-xs text-sky-700">
            Re-importing will not change this — it is the shape of the export, not a
            failed import. Open Actions → Manage files to see what each file was read
            as and where its rows went.
          </p>
        </div>
      )}

      {/* Counterparties table on top — or a dropzone when there's no data yet. */}
      {hasFiles ? (
        <WalletCounterparties slug={wallet.slug} name={wallet.name} />
      ) : (
        <div className="bg-white rounded border p-5 space-y-3">
          <div>
            <h2 className="font-semibold">Counterparties paid through {wallet.name}</h2>
            <p className="text-xs text-gray-400">
              No {wallet.name} data yet. Drop the Deel/Wise CSV exports below to
              import them — you can select several at once.
            </p>
          </div>
          <WalletUploader slug={wallet.slug} onStarted={afterUploadStarted} />
        </div>
      )}

      {wallet.healthAlarms.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded p-4 space-y-2">
          <h2 className="font-semibold text-amber-900">Health alerts</h2>
          {wallet.healthAlarms.map((alarm, i) => (
            <div key={`${alarm.kind}-${alarm.period}-${i}`} className="flex items-start gap-2 text-sm">
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                  alarm.kind === "MISSING_IMPORT"
                    ? "bg-amber-200 text-amber-900"
                    : "bg-red-200 text-red-900"
                }`}
              >
                {alarm.kind === "MISSING_IMPORT" ? "Missing import" : "Spurious outflow"}
              </span>
              <span className="text-amber-900">
                {alarm.period} — {alarm.message}
              </span>
            </div>
          ))}
        </div>
      )}

      {wallet.balance.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-4 bg-white rounded border p-4">
            <span className="text-xs font-medium text-gray-500">Show flows</span>
            {FLOWS.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={visible[f.key]}
                  onChange={(e) => setVisible((v) => ({ ...v, [f.key]: e.target.checked }))}
                />
                <span style={{ color: f.color }}>{f.label}</span>
              </label>
            ))}
          </div>

          <KpiChart
            key={series.map((s) => s.name).join("|")}
            title={`${wallet.name} balance (line) and monthly flows (bars)`}
            series={series}
            format="money"
            onMonthClick={() => {}}
          />
        </div>
      )}

      {wallet.reconciliation.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-semibold">Funding reconciliation</h2>
          <div className="bg-white rounded border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 uppercase border-b">
                  <th className="px-3 py-2">Run</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2 text-right">Funded</th>
                  <th className="px-3 py-2 text-right">Spent</th>
                  <th className="px-3 py-2 text-right">Delta</th>
                  <th className="px-3 py-2">Funding</th>
                </tr>
              </thead>
              <tbody>
                {wallet.reconciliation.map((r) => {
                  const off = r.delta !== 0 || !r.fundingMatched;
                  return (
                    <tr key={r.rec} className={`border-b last:border-b-0 ${off ? "bg-amber-50" : ""}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap">{r.rec}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">{r.date ?? "—"}</td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">{money(r.funded)}</td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">{money(r.spent)}</td>
                      <td className={`px-3 py-1.5 text-right whitespace-nowrap ${r.delta !== 0 ? "text-amber-700 font-medium" : "text-gray-400"}`}>
                        {money(r.delta)}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {r.fundingMatched ? (
                          <span className="text-xs text-green-700">matched</span>
                        ) : (
                          <span className="text-xs text-amber-700">unpaired</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {uploadOpen && (
        <Dialog.Root open onOpenChange={(open) => !open && setUploadOpen(false)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(38rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-5 shadow-xl">
              <div className="mb-3 flex items-center justify-between">
                <Dialog.Title className="font-semibold text-gray-900">
                  Upload {wallet.name} files
                </Dialog.Title>
                <button
                  type="button"
                  onClick={() => setUploadOpen(false)}
                  aria-label="Close"
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <WalletUploader slug={wallet.slug} onStarted={afterUploadStarted} />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {ledgerOpen && (
        <WalletLedgerDialog
          slug={wallet.slug}
          name={wallet.name}
          series={series}
          onClose={() => setLedgerOpen(false)}
        />
      )}

      {manageOpen && (
        <WalletManageFilesDialog
          slug={wallet.slug}
          name={wallet.name}
          onClose={() => setManageOpen(false)}
        />
      )}
    </div>
  );
}

const money = (n: number) =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
